import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Code,
  Eye,
  Heading1,
  Type,
  Image,
  Link as LinkIcon,
  Square,
  Trash2,
  GripVertical,
  Plus,
  Columns,
  Space,
  Facebook
} from 'lucide-react';

const COMPONENT_TYPES = [
  { type: 'heading', icon: Heading1, label: 'Heading', defaultContent: 'Heading Text' },
  { type: 'text', icon: Type, label: 'Text', defaultContent: 'Enter your text here...' },
  { type: 'button', icon: Square, label: 'Button', defaultContent: 'Click Here' },
  { type: 'image', icon: Image, label: 'Image', defaultContent: 'https://via.placeholder.com/600x200' },
  { type: 'divider', icon: LinkIcon, label: 'Divider', defaultContent: '' },
  { type: 'spacer', icon: Space, label: 'Spacer', defaultContent: '20' },
  { type: 'columns', icon: Columns, label: '2 Columns', defaultContent: '' },
  { type: 'social', icon: Facebook, label: 'Social Links', defaultContent: '' },
];

export default function EmailTemplateBuilder({ value, onChange, availableVariables = [] }) {
  const [mode, setMode] = useState('builder');
  const [components, setComponents] = useState([]);
  const [htmlCode, setHtmlCode] = useState(value || '');

  useEffect(() => {
    // Parse existing HTML into components
    if (value && components.length === 0) {
      setHtmlCode(value);
      parseHTMLToComponents(value);
    }
  }, [value]);

  const parseHTMLToComponents = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const contentDiv = doc.querySelector('.content') || doc.body;
    
    const parsedComponents = [];
    let id = 0;

    const parseNode = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        
        if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3') {
          parsedComponents.push({
            id: `comp-${id++}`,
            type: 'heading',
            content: node.textContent.trim()
          });
        } else if (tagName === 'p') {
          const text = node.textContent.trim();
          if (text && !node.querySelector('a.button')) {
            parsedComponents.push({
              id: `comp-${id++}`,
              type: 'text',
              content: text
            });
          }
        } else if (tagName === 'a' && node.className.includes('button')) {
          parsedComponents.push({
            id: `comp-${id++}`,
            type: 'button',
            content: node.textContent.trim(),
            url: node.getAttribute('href') || '#'
          });
        } else if (tagName === 'img') {
          parsedComponents.push({
            id: `comp-${id++}`,
            type: 'image',
            content: node.getAttribute('src') || '',
            alt: node.getAttribute('alt') || 'Image'
          });
        } else if (tagName === 'hr') {
          parsedComponents.push({
            id: `comp-${id++}`,
            type: 'divider',
            content: ''
          });
        } else if (tagName === 'table') {
          const rows = node.querySelectorAll('tr');
          rows.forEach(row => {
            const cells = row.querySelectorAll('td');
            cells.forEach(cell => {
              Array.from(cell.childNodes).forEach(parseNode);
            });
          });
        } else if (tagName === 'ul' || tagName === 'ol') {
          const items = Array.from(node.querySelectorAll('li')).map(li => '• ' + li.textContent.trim()).join('\n');
          if (items) {
            parsedComponents.push({
              id: `comp-${id++}`,
              type: 'text',
              content: items
            });
          }
        } else if (tagName === 'div') {
          // Check for special divs
          const style = node.getAttribute('style') || '';
          if (style.includes('height') && !node.textContent.trim()) {
            const height = style.match(/height:\s*(\d+)/)?.[1] || '20';
            parsedComponents.push({
              id: `comp-${id++}`,
              type: 'spacer',
              content: height
            });
          } else if (node.className?.includes('columns') || node.className?.includes('table')) {
            const columns = node.querySelectorAll('.column, td');
            if (columns.length >= 2) {
              parsedComponents.push({
                id: `comp-${id++}`,
                type: 'columns',
                leftContent: columns[0].innerHTML.trim(),
                rightContent: columns[1].innerHTML.trim()
              });
            } else {
              Array.from(node.childNodes).forEach(parseNode);
            }
          } else {
            Array.from(node.childNodes).forEach(parseNode);
          }
        } else {
          Array.from(node.childNodes).forEach(parseNode);
        }
      }
    };

    Array.from(contentDiv.childNodes).forEach(parseNode);
    
    if (parsedComponents.length > 0) {
      setComponents(parsedComponents);
    }
  };

  const generateHTML = (comps) => {
    const baseStyles = `
      body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; background: #f8fafc; margin: 0; padding: 20px; }
      .container { max-width: 600px; margin: 0 auto; background: white; }
      .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px 20px; text-align: center; }
      .content { padding: 30px 20px; }
      .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      .button { display: inline-block; background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; }
      img { max-width: 100%; height: auto; }
      hr { border: none; border-top: 2px solid #e2e8f0; margin: 20px 0; }
      .columns { display: table; width: 100%; margin: 20px 0; }
      .column { display: table-cell; width: 50%; padding: 10px; vertical-align: top; }
      .social-links { text-align: center; margin: 20px 0; }
      .social-links a { display: inline-block; margin: 0 10px; text-decoration: none; }
    `;

    const componentHTML = comps.map(comp => {
      switch (comp.type) {
        case 'heading':
          return `<h2 style="color: #1e293b; margin: 20px 0 10px 0;">${comp.content}</h2>`;
        case 'text':
          return `<p style="margin: 10px 0;">${comp.content}</p>`;
        case 'button':
          return `<div style="text-align: center; margin: 20px 0;"><a href="${comp.url || '#'}" class="button">${comp.content}</a></div>`;
        case 'image':
          return `<div style="margin: 20px 0;"><img src="${comp.content}" alt="${comp.alt || 'Image'}" /></div>`;
        case 'divider':
          return `<hr />`;
        case 'spacer':
          return `<div style="height: ${comp.content || 20}px;"></div>`;
        case 'columns':
          return `<div class="columns">
            <div class="column">${comp.leftContent || 'Left column content'}</div>
            <div class="column">${comp.rightContent || 'Right column content'}</div>
          </div>`;
        case 'social':
          const links = [];
          if (comp.facebook) links.push(`<a href="${comp.facebook}"><img src="https://cdn-icons-png.flaticon.com/32/733/733547.png" alt="Facebook" width="32" height="32"/></a>`);
          if (comp.twitter) links.push(`<a href="${comp.twitter}"><img src="https://cdn-icons-png.flaticon.com/32/733/733579.png" alt="Twitter" width="32" height="32"/></a>`);
          if (comp.linkedin) links.push(`<a href="${comp.linkedin}"><img src="https://cdn-icons-png.flaticon.com/32/733/733561.png" alt="LinkedIn" width="32" height="32"/></a>`);
          if (comp.instagram) links.push(`<a href="${comp.instagram}"><img src="https://cdn-icons-png.flaticon.com/32/733/733558.png" alt="Instagram" width="32" height="32"/></a>`);
          return `<div class="social-links">${links.join('')}</div>`;
        default:
          return '';
      }
    }).join('\n');

    return `<!DOCTYPE html>
<html>
<head>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">Email Title</h1>
    </div>
    <div class="content">
      ${componentHTML}
    </div>
    <div class="footer">
      <p>&copy; ${new Date().getFullYear()} Your Company. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setComponents(items);
    const html = generateHTML(items);
    setHtmlCode(html);
    onChange(html);
  };

  const addComponent = (type) => {
    const template = COMPONENT_TYPES.find(t => t.type === type);
    const newComponent = {
      id: `comp-${Date.now()}`,
      type,
      content: template.defaultContent,
      url: type === 'button' ? '#' : undefined,
      alt: type === 'image' ? 'Image' : undefined,
      leftContent: type === 'columns' ? 'Left column content' : undefined,
      rightContent: type === 'columns' ? 'Right column content' : undefined,
      facebook: type === 'social' ? '' : undefined,
      twitter: type === 'social' ? '' : undefined,
      linkedin: type === 'social' ? '' : undefined,
      instagram: type === 'social' ? '' : undefined
    };
    
    const updated = [...components, newComponent];
    setComponents(updated);
    const html = generateHTML(updated);
    setHtmlCode(html);
    onChange(html);
  };

  const updateComponent = (id, field, value) => {
    const updated = components.map(comp =>
      comp.id === id ? { ...comp, [field]: value } : comp
    );
    setComponents(updated);
    const html = generateHTML(updated);
    setHtmlCode(html);
    onChange(html);
  };

  const deleteComponent = (id) => {
    const updated = components.filter(comp => comp.id !== id);
    setComponents(updated);
    const html = generateHTML(updated);
    setHtmlCode(html);
    onChange(html);
  };

  const handleHTMLChange = (html) => {
    setHtmlCode(html);
    onChange(html);
  };

  const insertVariable = (variable) => {
    if (mode === 'html') {
      setHtmlCode(prev => prev + `{{${variable}}}`);
      onChange(htmlCode + `{{${variable}}}`);
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="builder">
            <Eye className="w-4 h-4 mr-2" />
            Visual Builder
          </TabsTrigger>
          <TabsTrigger value="html">
            <Code className="w-4 h-4 mr-2" />
            HTML Code
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-4">
          {/* Component Palette */}
          <Card className="p-4">
            <Label className="mb-3 block">Add Components</Label>
            <div className="flex flex-wrap gap-2">
              {COMPONENT_TYPES.map(comp => {
                const Icon = comp.icon;
                return (
                  <Button
                    key={comp.type}
                    variant="outline"
                    size="sm"
                    onClick={() => addComponent(comp.type)}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {comp.label}
                  </Button>
                );
              })}
            </div>
          </Card>

          {/* Canvas */}
          <Card className="p-4">
            <Label className="mb-3 block">Email Content</Label>
            {components.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Plus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Add components to start building your email</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="email-components">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-3"
                    >
                      {components.map((component, index) => (
                        <Draggable
                          key={component.id}
                          draggableId={component.id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="bg-slate-50 border border-slate-200 rounded-lg p-4"
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="text-slate-400 mt-2 cursor-grab active:cursor-grabbing"
                                >
                                  <GripVertical className="w-5 h-5" />
                                </div>

                                <div className="flex-1 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm text-slate-600 capitalize">
                                      {component.type}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteComponent(component.id)}
                                      className="text-red-500 hover:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>

                                  {component.type === 'heading' && (
                                    <Input
                                      value={component.content}
                                      onChange={(e) =>
                                        updateComponent(component.id, 'content', e.target.value)
                                      }
                                      placeholder="Heading text"
                                    />
                                  )}

                                  {component.type === 'text' && (
                                    <Textarea
                                      value={component.content}
                                      onChange={(e) =>
                                        updateComponent(component.id, 'content', e.target.value)
                                      }
                                      placeholder="Your text content"
                                      rows={3}
                                    />
                                  )}

                                  {component.type === 'button' && (
                                    <div className="space-y-2">
                                      <Input
                                        value={component.content}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'content', e.target.value)
                                        }
                                        placeholder="Button text"
                                      />
                                      <Input
                                        value={component.url}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'url', e.target.value)
                                        }
                                        placeholder="Button URL"
                                      />
                                    </div>
                                  )}

                                  {component.type === 'image' && (
                                    <div className="space-y-2">
                                      <Input
                                        value={component.content}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'content', e.target.value)
                                        }
                                        placeholder="Image URL"
                                      />
                                      <Input
                                        value={component.alt || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'alt', e.target.value)
                                        }
                                        placeholder="Alt text"
                                      />
                                    </div>
                                  )}

                                  {component.type === 'spacer' && (
                                    <Input
                                      type="number"
                                      value={component.content}
                                      onChange={(e) =>
                                        updateComponent(component.id, 'content', e.target.value)
                                      }
                                      placeholder="Height in pixels"
                                    />
                                  )}

                                  {component.type === 'columns' && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <Textarea
                                        value={component.leftContent || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'leftContent', e.target.value)
                                        }
                                        placeholder="Left column"
                                        rows={3}
                                      />
                                      <Textarea
                                        value={component.rightContent || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'rightContent', e.target.value)
                                        }
                                        placeholder="Right column"
                                        rows={3}
                                      />
                                    </div>
                                  )}

                                  {component.type === 'social' && (
                                    <div className="space-y-2">
                                      <Input
                                        value={component.facebook || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'facebook', e.target.value)
                                        }
                                        placeholder="Facebook URL"
                                      />
                                      <Input
                                        value={component.twitter || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'twitter', e.target.value)
                                        }
                                        placeholder="Twitter URL"
                                      />
                                      <Input
                                        value={component.linkedin || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'linkedin', e.target.value)
                                        }
                                        placeholder="LinkedIn URL"
                                      />
                                      <Input
                                        value={component.instagram || ''}
                                        onChange={(e) =>
                                          updateComponent(component.id, 'instagram', e.target.value)
                                        }
                                        placeholder="Instagram URL"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="html" className="space-y-4">
          <Textarea
            value={htmlCode}
            onChange={(e) => handleHTMLChange(e.target.value)}
            className="font-mono text-sm min-h-[400px]"
            placeholder="Enter HTML code..."
          />
        </TabsContent>
      </Tabs>

      {/* Variables */}
      {availableVariables.length > 0 && (
        <Card className="p-4">
          <Label className="mb-2 block">Insert Variables</Label>
          <div className="flex flex-wrap gap-2">
            {availableVariables.map((variable) => (
              <Button
                key={variable}
                variant="outline"
                size="sm"
                onClick={() => insertVariable(variable)}
              >
                {`{{${variable}}}`}
              </Button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}